import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { putAuth } from '@/api/ApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { queryClient } from '@/hooks/QueryClient';
import { fundIsraeliIdChangeRequestsUrl } from '@/hooks/useFundIsraeliIdChangeRequests';
import { formatDateLocale } from '@/helpers/dateHelper';
import { SupportedIsraeliIdChangeRequestDtoIn } from '@/models/in/SupportedIsraeliIdChangeRequestDtoIn';
import styles from './IsraeliIdChangeDecisionNotice.module.css';

interface IsraeliIdChangeDecisionNoticeProps {
  fundId: number;
  request: SupportedIsraeliIdChangeRequestDtoIn;
  /** Opens the request dialog - after a rejection, to send the right number. */
  onRequestAgain: () => void;
}

/**
 * The office's decision on a coordinator's ID correction request, shown once in the supported's
 * details (HAT-1428). It stays until «הבנתי», or until a new request for the supported - and then
 * it is gone, instead of greeting the coordinator for as long as the supported exists.
 */
const IsraeliIdChangeDecisionNotice = ({ fundId, request, onRequestAgain }: IsraeliIdChangeDecisionNoticeProps) => {
  const { t, i18n } = useTranslation();
  const { currentUser } = useAuthContext();
  const [isSaving, setIsSaving] = useState(false);
  const isApproved = request.status === 'Approved';
  const prefix = 'israeliIdChangeRequest.decision';

  const acknowledge = async () => {
    setIsSaving(true);
    try {
      await putAuth(
        `funds/${fundId}/supporteds/${request.contactId}/israeli-id-change-request/acknowledge`,
        { Authorization: `Bearer ${currentUser?.token}` }
      );
      await queryClient.invalidateQueries({ queryKey: [fundIsraeliIdChangeRequestsUrl(fundId)] });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={`${styles.notice} ${isApproved ? styles.approved : styles.rejected}`} role="status">
      <p className={styles.title}>
        {t(isApproved ? `${prefix}.approvedTitle` : `${prefix}.rejectedTitle`, { id: request.requestedIsraeliId })}
      </p>
      {!isApproved && request.rejectionReason && (
        <p className={styles.reason}>{t(`${prefix}.reason`, { reason: request.rejectionReason })}</p>
      )}
      {request.reviewedAt && (
        <p className={styles.date}>{t(`${prefix}.decidedOn`, { date: formatDateLocale(request.reviewedAt, i18n.language) })}</p>
      )}
      <div className={styles.actions}>
        {!isApproved && (
          <button type="button" className={styles.primary} onClick={onRequestAgain} disabled={isSaving}>
            {t(`${prefix}.requestAgain`)}
          </button>
        )}
        <button type="button" className={styles.secondary} onClick={acknowledge} disabled={isSaving}>
          {t(`${prefix}.acknowledge`)}
        </button>
      </div>
    </div>
  );
};

export default IsraeliIdChangeDecisionNotice;
